import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectIsEmbeddedMessagingJourneysFeatureFlagOn,
  selectIsEmbeddedMessagingCoreFeatureFlagOn,
  selectIsAllCombinatorSearchQueryFeatureFlagOn,
  selectIsSupportChatTestFeatureFlagOn,
  selectIsJourneyGoalsFeatureFlagOn,
  selectIsHoldUntilReplyTileFFOn,
} from 'core/userContext/selectors';
import { FlowChartDndContainer } from 'ui/workflows/flowchart';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { FB_WORKFLOWS_ALLOWED_NODE_TYPES, NODE_TYPES, getNodeSections } from '../../constants';
import {
  fetchBetaNodesRequest,
  getClonedFromJourneyInvalidNodes,
  getIsFacebookWorkflowsFeatureFlagEnabled,
  getJourneyCurrentConversionEventName,
} from '../../ducks';
import { ReviewNeeded } from './ReviewNeeded';
import Tile from './Tile';
import { NodeTilePanelStyled } from './TileSidebar.styles';
import { AttachedJourneyConversion } from './AttachedJourneyConversion';

const TileSidebarCmp = () => {
  const dispatch = useDispatch();
  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isFacebookWorkflowsEnabled = useSelector(getIsFacebookWorkflowsFeatureFlagEnabled);
  const isSupportChatTestEnabled = useSelector(selectIsSupportChatTestFeatureFlagOn);
  const isJourneyEmbeddedMessagingEnabled = useSelector(
    selectIsEmbeddedMessagingJourneysFeatureFlagOn,
  );
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const invalidClonedNodes = useSelector(getClonedFromJourneyInvalidNodes);
  const isAllEmptySearchQueryEnabled = useSelector(selectIsAllCombinatorSearchQueryFeatureFlagOn);
  const currentConversionEventName = useSelector(getJourneyCurrentConversionEventName);
  const { isWhatsAppEnabled: isWhatsAppMessageChannelEnabled } = useWhatsAppMessageChannelFeature();
  const isHoldUntilReplyTileEnabled = useSelector(selectIsHoldUntilReplyTileFFOn);

  useEffect(() => {
    // fetch the list of nodes that are supported in beta
    dispatch(fetchBetaNodesRequest());
  }, []);

  const sections = useMemo(() => {
    const excludedNodes = [
      ...(isFacebookWorkflowsEnabled ? [] : FB_WORKFLOWS_ALLOWED_NODE_TYPES),
      ...(isJourneyEmbeddedMessagingEnabled && isEmbeddedMessagingEnabled
        ? []
        : [NODE_TYPES.SEND_EMBEDDED]),
      ...(isWhatsAppMessageChannelEnabled ? [] : [NODE_TYPES.SEND_WHATSAPP]),
      ...(isHoldUntilReplyTileEnabled ? [] : [NODE_TYPES.HOLD_UNTIL_REPLY]),
    ];
    return getNodeSections(excludedNodes, isAllEmptySearchQueryEnabled);
  }, [
    isFacebookWorkflowsEnabled,
    isAllEmptySearchQueryEnabled,
    isJourneyEmbeddedMessagingEnabled,
    isEmbeddedMessagingEnabled,
    isWhatsAppMessageChannelEnabled,
    isHoldUntilReplyTileEnabled,
  ]);

  return (
    <FlowChartDndContainer className="tile-sidebar">
      <NodeTilePanelStyled
        sections={sections}
        TileComponent={Tile}
        supportChatEnabled={isSupportChatTestEnabled}
        showReviewNeeded={!!invalidClonedNodes?.length}
        ReviewNeededComponent={ReviewNeeded}
        hasAttachedJourneyConversion={!!isJourneyGoalsEnabled && !!currentConversionEventName}
        AttachedJourneyConversion={AttachedJourneyConversion}
      />
    </FlowChartDndContainer>
  );
};

export const TileSidebar = React.memo(TileSidebarCmp);

export default TileSidebar;
